/**
 * @file This file is a helper class for its sibling class Table.ts. It provides the logic needed to support the multi-view single-table scenario.
 */

import i18next from 'i18next';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import {
    PropertyMessageType,
    SchemaTag,
    type GenerateAppSchemaParameters,
    type ProcessingRule,
    type SchemaHandlingParams,
    type SyncRule
} from '@sap/ux-specification-types';
import type * as v2 from '@sap/ux-specification-types/src/v2/genericSchemaHandling/controls/Table';
import { getListOfValidAnnotations } from '../../../common';
import { syncRule } from '../../../common/decoration';
import type { TableParams } from './Table';
import { getManifestPathToPageSettings, getManifestPropertyByPath, isEmpty } from '../../utils';
import { handleDisallowedProperty, hasPropertyTypeError } from '../../generate/manifestPropertyUtils';

/**
 * An instance of this type collects information relevant for quickVariantSelection for a table.
 *
 * @property suffix - Only used on OP to identify the facet the table belongs to
 * @property selectionVariants - The list of valid selection variant annotations that can be used as annotationPath in quickVariantSelection
 */
type QuickVariantSelectionParams = {
    suffix?: string;
    selectionVariants: string[];
};

export class ViewOnTable implements v2.ViewOnTable {
    key: string;

    //annotationPath
    @syncRule({
        manifest: {},
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const quickVariantSelectionParams = schemaHandlingParams.specificParams as QuickVariantSelectionParams;
            processingRule.element.enum = quickVariantSelectionParams.selectionVariants;
        }
    })
    annotationPath: string;
}

export class MultiViewsOnTable implements v2.MultiViewsOnTable {
    // showCounts
    @syncRule({
        manifest: {},
        processingRuleAdapter(
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams,
            generateParameters: GenerateAppSchemaParameters
        ): void {
            processingRule.manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
                'quickVariantSelection'
            ]);
            const quickVariantSelection = getManifestPropertyByPath(
                generateParameters.manifest,
                processingRule.manifestPath
            );
            if (
                !hasPropertyTypeError(quickVariantSelection, 'showCounts', generateParameters.logger, 'boolean') &&
                !hasPropertyTypeError(quickVariantSelection, 'variants', generateParameters.logger, 'object') &&
                quickVariantSelection?.['showCounts'] !== undefined &&
                isEmpty(quickVariantSelection['variants'])
            ) {
                processingRule.element[SchemaTag.messages] = [
                    {
                        text: i18next.t('QUICKVARIANTSELECTIONNOVARIANTS'),
                        deletable: true,
                        type: PropertyMessageType.Warning
                    }
                ];
            }
        }
    })
    showCounts?: boolean;

    // variants
    @syncRule({
        manifest: {},
        processingRuleAdapter(processingRule: ProcessingRule, schemaHandlingParams: SchemaHandlingParams): void {
            const quickVariantSelectionParams = schemaHandlingParams.specificParams as QuickVariantSelectionParams;
            processingRule.manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath, [
                'quickVariantSelection'
            ]);
            processingRule.additionalPropertiesAdaptation = {
                suffix: quickVariantSelectionParams.suffix,
                syncRuleProvider: ViewOnTable
            };
        }
    })
    variants: {
        [key: string]: v2.ViewOnTable;
    };
}

/**
 * Provides the syncRule for the quickVariantSelection property of a table.
 *
 * @returns the syncRule
 */
export function getSyncRuleForQuickVariantSelection(): SyncRule {
    return {
        processingRuleAdapter(
            processingRule: ProcessingRule,
            schemaHandlingParams: SchemaHandlingParams,
            generateParameters: GenerateAppSchemaParameters
        ): void {
            const tableParams = schemaHandlingParams.specificParams as TableParams;
            let disallowedReason: string | undefined;
            let quickVariantSelectionParams: QuickVariantSelectionParams | undefined;
            if (tableParams.multiTableInfo) {
                if (tableParams.multiTableInfo.variantInfo) {
                    // The quickVariantSelection property is not valid for the views in a multi-table scenario
                    delete processingRule.element;
                    return;
                }
                disallowedReason = i18next.t('QUICKVARIANTSELECTIONDUPLICATE');
            } else {
                const selectionVariants = getListOfValidAnnotations(tableParams.entityType, [
                    UIAnnotationTerms.SelectionPresentationVariant,
                    UIAnnotationTerms.SelectionVariant
                ]);
                if (selectionVariants.length === 0) {
                    disallowedReason = i18next.t('QUICKVARIANTSELECTIONNOSELECTIONVARIANTANNOTATIONS');
                }
                quickVariantSelectionParams = {
                    // suffix: tableParams.facet?.stableIdPart, only relevant for tables on OP
                    selectionVariants
                };
            }
            if (disallowedReason) {
                const manifestPath = getManifestPathToPageSettings(schemaHandlingParams.pageInfo.pagePath);
                const { value } = getManifestPropertyByPath(
                    generateParameters.manifest,
                    manifestPath,
                    'quickVariantSelection'
                );
                handleDisallowedProperty(disallowedReason, processingRule, value, manifestPath);
                processingRule.element[SchemaTag.artifactType] = 'Manifest';
            } else {
                processingRule.referenceAdaptation = {
                    suffix: quickVariantSelectionParams.suffix,
                    syncRuleProvider: MultiViewsOnTable,
                    specificParams: quickVariantSelectionParams
                };
            }
        }
    };
}
